import React, { useEffect, useState, useMemo } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { Box, styled } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { FormConfigProps } from "../../../../../../../shared/components/form-builder/form-builder.model";

interface IncomingTcInfoProps {
  formRef: React.RefObject<any>;
  componentData: any;
  setComponentData: (data: any) => void;
}

interface Option {
  value: string;
  label: string;
  tcId?: string;
  atcId?: string;
  ledgerTransactionId?: string;
  // For RMDF search results, carry rawMaterials so we can build productNumber options
  rmdfRawMaterials?: any[];
}

const CERTIFICATE_TYPES = [
  { value: 'GOTS', label: 'GOTS' },
  { value: 'ATU', label: 'ATU' },
  { value: 'TC', label: 'TC' }
];

const IncomingTcInfo: React.FC<IncomingTcInfoProps> = ({
  formRef,
  componentData,
  setComponentData,
}) => {
  // Initialize isFlagSellerState from componentData.isFlagSeller (which comes from API)
  const [isFlagSellerState, setIsFlagSellerState] = useState<string>(componentData.isFlagSeller || "No");
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [incomingProductOptions, setIncomingProductOptions] = useState<Option[]>([]);
  const [incomingStandards, setIncomingStandards] = useState<string[]>([]);
  const [disableIncomingProduct, setDisableIncomingProduct] = useState(true);
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState<"tc" | "atc" | "rmdf" | null>(null);
  const [licensingCodes, setLicensingCodes] = useState<Option[]>([]);
  const [selectedCertType, setSelectedCertType] = useState<string>("");
  const [selectedLicensingCode, setSelectedLicensingCode] = useState<string>("");
  const [productIncomingTcNo, setProductIncomingTcNo] = useState<string>("");
  const [hasRMDF, setHasRMDF] = useState(false);

  const { TE_ID, formtype } = useParams();
  const { executeGet } = useApi();
  const { cbLicensingCode } = useTransactionCertificateStore();
  const theme = useTheme();
  const hasSubmitTc: boolean = formtype === "submited-tc";

  const fetchTcBaseDetails = async () => {
    if (!TE_ID) return;
    try {
      const response = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: TE_ID });
      const data = response?.data;
      const standards = data?.tcStandard || [];
      const hasGRSorRCS = standards.includes("GRS") || standards.includes("RCS");
      setHasRMDF(hasGRSorRCS);
    } catch (err) {
      console.error("Failed to fetch TC base details:", err);
    }
  };

  const certificateTypes = useMemo(() => {
    const types = [...CERTIFICATE_TYPES];
    if (hasRMDF) {
      types.push({ value: 'RMDF', label: 'RMDF' });
    }
    return types;
  }, [hasRMDF]);

  const fetchProductOptions = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      const options: Option[] =
        (response?.data?.response || []).map((item: any) => ({
          value: item.tcProductArticleNo,
          label: item.tcProductArticleNo,
        })) || [];

      // remove duplicates
      const uniqueOptions = options.filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      setProductOptions(uniqueOptions);
    } catch (err) {
      console.error("Failed to fetch product options:", err);
    }
  };

  const normalizeStandards = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    // Flatten nested arrays and convert values to strings
    if (typeof (Array.prototype as any).flat === "function") {
      return (arr as any).flat(Infinity).filter((v: any) => v !== null && v !== undefined).map(String);
    }
    return (arr as any).reduce((acc: any[], cur: any) => acc.concat(Array.isArray(cur) ? cur : [cur]), []).filter((v: any) => v !== null && v !== undefined).map(String);
  };

  const fetchIncomingATCDetails = async (atcId: string) => {
    if (!atcId) return;
    setLoadingStandards(true);

    try {
      const response = await executeGet(TCAppConfig.GET_ATC_DETAILS, { atcId });
      
      // Parse the stringified JSON response
      let data = response?.data;
      if (data?.response && typeof data.response === 'string') {
        try {
          data = JSON.parse(data.response);
        } catch (parseError) {
          console.error("Failed to parse ATC details response:", parseError);
          throw new Error("Invalid response format");
        }
      }

      // Handle atcStandard as either an array or a single value
      const standards = Array.isArray(data?.atcStandard) 
        ? data.atcStandard 
        : data?.atcStandard 
        ? [data.atcStandard] 
        : [];
      setIncomingStandards(standards);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", standards);

      const productOptions: Option[] =
        (data?.productInformation || []).map((item: any) => ({
          value: item.atcProductNo,
          label: item.atcProductNo,
        })) || [];

   
      const uniqueOptions = productOptions.filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      setIncomingProductOptions(uniqueOptions);
      setDisableIncomingProduct(uniqueOptions.length === 0);
    } catch (err) {
      console.error("Failed to fetch incoming ATC details:", err);
      setIncomingStandards([]);
      setIncomingProductOptions([]);
      setDisableIncomingProduct(true);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
    } finally {
      setLoadingStandards(false);
    }
  };

  const fetchIncomingStandards = async (id: string) => {
    if (!id) return;
    setLoadingStandards(true);

    try {
      const [v4Response, baseResponse] = await Promise.allSettled([
        executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, { tcId: id }),
        executeGet(TCAppConfig.GET_TC_BASE_DETAILS, { tcId: id }),
      ]);

      let standards: string[] = [];

      // Process V4 response
      if (v4Response.status === 'fulfilled') {
        const v4Data = v4Response.value?.data;
        const v4Standards =
          Array.isArray(v4Data?.tcStandardDescription) && v4Data.tcStandardDescription.length > 0
            ? v4Data.tcStandardDescription
            : Array.isArray(v4Data?.tcStandard)
            ? v4Data.tcStandard
            : [];
        standards = [...standards, ...normalizeStandards(v4Standards)];
      } else {
        console.error("Failed to fetch from GET_TC_BASE_DETAILS_V4:", v4Response.reason);
      }

      // Process base response
      if (baseResponse.status === 'fulfilled') {
        const baseData = baseResponse.value?.data;
        const baseStandards =
          Array.isArray(baseData?.tcStandardDescription) && baseData.tcStandardDescription.length > 0
            ? baseData.tcStandardDescription
            : Array.isArray(baseData?.tcStandard)
            ? baseData.tcStandard
            : [];
        standards = [...standards, ...normalizeStandards(baseStandards)];
      } else {
        console.error("Failed to fetch from GET_TC_BASE_DETAILS:", baseResponse.reason);
      }

      const uniqueStandards = [...new Set(standards)];
      setIncomingStandards(uniqueStandards);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", uniqueStandards);
    } catch (err) {
      console.error("Failed to fetch incoming TC standards:", err);
      setIncomingStandards([]);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
    } finally {
      setLoadingStandards(false);
    }
  };

  const fetchIncomingProductArticles = async (id: string) => {
    if (!id) return;
    try {
      const [v4Response, rmResponse] = await Promise.allSettled([
        executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: id }),
        executeGet(TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST, { tcId: id }),
      ]);

      let allOptions: Option[] = [];

      if (v4Response.status === 'fulfilled') {
        const v4Data = v4Response.value?.data;
        const v4ResponseArray = v4Data?.response || v4Data || [];
        const v4Options: Option[] =
          (Array.isArray(v4ResponseArray) ? v4ResponseArray : []).map((item: any) => ({
            value: item.tcProductArticleNo,
            label: item.tcProductArticleNo,
          })) || [];
        allOptions = [...allOptions, ...v4Options];
        console.log("V4 Product Options:", v4Options);
      } else {
        console.error("Failed to fetch from GET_LIST_PRODUCT_INFO_V4:", v4Response.reason);
      }

      if (rmResponse.status === 'fulfilled') {
        const rmData = rmResponse.value?.data;
        const rmResponseArray = rmData?.response || rmData || [];
        const rmOptions: Option[] =
          (Array.isArray(rmResponseArray) ? rmResponseArray : []).map((item: any) => ({
            value: item.tcProductNo,
            label: item.tcProductNo,
          })) || [];
        allOptions = [...allOptions, ...rmOptions];
      } else {
        console.error("Failed to fetch from GET_PRODUCT_RAWMATERIAL_INFO_LIST:", rmResponse.reason);
      }

      const uniqueOptions = allOptions.filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      console.log("All unique options:", uniqueOptions);
      setIncomingProductOptions(uniqueOptions);
      setDisableIncomingProduct(uniqueOptions.length === 0);
    } catch (err) {
      console.error("Failed to fetch incoming product articles:", err);
      setIncomingProductOptions([]);
      setDisableIncomingProduct(true);
    }
  };


  const getTCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    if(value === 'na') return [];
    try {
      const queryParams: Record<string, any> = {
        tcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_TC_NO_INPUT_REF, queryParams);
      return response.data?.map((item: any) => ({
        label: item.tcNo,
        value: item.tcNo,
        tcId: item.tcId,
      })) || [];
    } catch (e) {
      console.error(`Error fetching Input TC No: ${e}`);
      return [];
    }
  };

  const getInputATCNoForSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        atcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        atcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_ATC_NO, queryParams);
      const allResults = response.data?.response || [];
      
      const filteredResults = allResults.filter((item: any) => {
        if (selectedCertType === 'GOTS') {
          return item.atcNo.startsWith('GOTS-');
        } else if (selectedCertType === 'ATU') {
          return item.atcNo.startsWith('ATU-');
        }
        return true; 
      });

      return filteredResults.map((item: any) => ({
        label: item.atcNo,
        value: item.atcNo,
        atcId: item.atcId,
      })) || [];
    } catch (e) {
      console.error(`Error fetching Input ATC No: ${e}`);
      return [];
    }
  };

  const getRMDFforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        tcEtcMdfNumber: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
      };

      const response: any = await executeGet(TCAppConfig.GET_RMDF_SEARCH, queryParams);
      const items = response?.data?.response || response?.data || [];

      return items.map((item: any) => {
        const rawMaterials = Array.isArray(item.rawMaterials) ? item.rawMaterials : [];
        const firstLedgerTxnId =
          rawMaterials.find((rm: any) => rm?.ledgerTransactionId)?.ledgerTransactionId ||
          item.ledgerTransactionId ||
          undefined;

        return {
          label: item.tcEtcMdfNumber,
          value: item.tcEtcMdfNumber,
          ledgerTransactionId: firstLedgerTxnId,
          rmdfRawMaterials: rawMaterials,
        } as Option;
      }) || [];
    } catch (e) {
      console.error(`Error fetching Input RMDF No: ${e}`);
      return [];
    }
  };

  const handleCertificateNumberChange = (selectedOption: Option | null) => {
    const value = selectedOption?.value || null;
    
    // Issue 2: If isFlagSellerState is 'Yes', ignore the change (disable all operations)
    if (isFlagSellerState === 'Yes') {
      return;
    }
    
    // Preserve the licensing code from both state and parent data
    const licensingCodeToPreserve = selectedLicensingCode || 
      (componentData.incomingTcLicensingCode ? 
        (componentData.incomingTcLicensingCode.startsWith("CB-") ? 
          componentData.incomingTcLicensingCode.replace("CB-", "") : 
          componentData.incomingTcLicensingCode) 
        : "");
    
    if (selectedCertType === 'TC') {
      formRef?.current?.setFieldValue("tcIncomingTCNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setActiveSearchField("tc");
        getTCNoforSearch(value, 0, "query", selectedLicensingCode).then((res) => {
          const selected = res.find((r) => r.value === value);
          if (selected?.tcId) {
            formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            // Propagate updated ID to parent component, clearing old product selections
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingTCNo: value,
              tcIncomingTcId: selected.tcId,
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(selected.tcId);
            fetchIncomingStandards(selected.tcId);
          } else {
            formRef?.current?.setFieldValue("tcIncomingTcId", "");
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingTCNo: value,
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        formRef?.current?.setFieldValue("tcIncomingTcId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({ 
          ...(componentData || {}), 
          tcIncomingTCNo: "",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'RMDF') {
      formRef?.current?.setFieldValue("tcIncomingRMDFNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setActiveSearchField("rmdf");
        getRMDFforSearch(value, 0, "query", selectedLicensingCode).then((res) => {
          const selected = res.find((r) => r.value === value);
          if (selected?.ledgerTransactionId) {
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", selected.ledgerTransactionId);
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }

            // Build Incoming Product Article Number options from RMDF rawMaterials.productNumber
            const rmdfRawMaterials = Array.isArray(selected.rmdfRawMaterials) ? selected.rmdfRawMaterials : [];
            const rmdfProductOptions: Option[] = rmdfRawMaterials
              .filter((rm: any) => rm?.productNumber !== undefined && rm?.productNumber !== null)
              .map((rm: any) => {
                const productNumber = String(rm.productNumber);
                return {
                  value: productNumber,
                  label: productNumber,
                } as Option;
              });

            // Remove duplicate product numbers
            const uniqueRmdfProductOptions = rmdfProductOptions.filter(
              (item, index, self) => index === self.findIndex((t) => t.value === item.value)
            );

            setIncomingProductOptions(uniqueRmdfProductOptions);
            setDisableIncomingProduct(uniqueRmdfProductOptions.length === 0);
            // Do NOT auto-populate standards for RMDF (requirement)

            // Propagate updated ID to parent component, clearing old product selections
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingRMDFNo: value,
              tcIncomingLedgerTransactionId: selected.ledgerTransactionId,
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          } else {
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingRMDFNo: value,
              tcIncomingLedgerTransactionId: "",
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({ 
          ...(componentData || {}), 
          tcIncomingRMDFNo: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
      formRef?.current?.setFieldValue("tcIncomingATCNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setActiveSearchField("atc");
        getInputATCNoForSearch(value, 0, "query", selectedLicensingCode).then((res) => {
          const selected = res.find((r) => r.value === value);
          if (selected?.atcId) {
            formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
            // Restore licensing code to form field after reset
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            // Propagate updated ID to parent component, clearing old product selections
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingATCNo: value,
              tcIncomingAtcId: selected.atcId,
              tcIncomingTcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingATCDetails(selected.atcId);
          } else {
            formRef?.current?.setFieldValue("tcIncomingAtcId", "");
            // Restore licensing code to form field
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({ 
              ...(componentData || {}), 
              tcIncomingATCNo: value,
              tcIncomingAtcId: "",
              tcIncomingTcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        formRef?.current?.setFieldValue("tcIncomingAtcId", "");
        // Restore licensing code to form field
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({ 
          ...(componentData || {}), 
          tcIncomingATCNo: "",
          tcIncomingAtcId: "",
          tcIncomingTcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    }
  };

  const resetIncomingFields = () => {
    setDisableIncomingProduct(true);
    setIncomingProductOptions([]);
    setIncomingStandards([]);
    formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
    formRef?.current?.setFieldValue("tcIncomingProductNo", []);
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
  };

  useEffect(() => {
    const incomingTCNo = componentData.tcIncomingTCNo;
    const incomingATCNo = componentData.tcIncomingATCNo;
    const incomingRMDFNo = componentData.tcIncomingRMDFNo;
    const licenseCodeRaw = componentData.incomingTcLicensingCode || componentData.licensingCode || "";

    const licenseValueForForm = licenseCodeRaw
      ? licenseCodeRaw.startsWith("CB-")
        ? licenseCodeRaw
        : `CB-${licenseCodeRaw}`
      : "";

    if (licenseValueForForm) {
      setSelectedLicensingCode(licenseValueForForm);

        try {
          formRef?.current?.setFieldValue("licensingCode", licenseValueForForm);
        } catch (e) {
          // ignore if formRef not yet available
        }
    }


    if (incomingTCNo) {
      setSelectedCertType('TC');
      formRef?.current?.setFieldValue("certificateType", 'TC');
      setActiveSearchField("tc");
      setProductIncomingTcNo(incomingTCNo);
    
      if (licenseValueForForm) {
        getTCNoforSearch(incomingTCNo, 0, "query", licenseValueForForm).then((res) => {
          const selected = res.find((r) => r.value === incomingTCNo);
          if (selected?.tcId) {
 
            try {
              formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
            } catch (e) {
              // ignore if formRef not ready
            }
            fetchIncomingProductArticles(selected.tcId);
            fetchIncomingStandards(selected.tcId);
          }
        });
      }
    } else if (incomingRMDFNo) {
      setSelectedCertType('RMDF');
      formRef?.current?.setFieldValue("certificateType", 'RMDF');
      setActiveSearchField("rmdf");
      setProductIncomingTcNo(incomingRMDFNo);
    
      if (licenseValueForForm) {
        getRMDFforSearch(incomingRMDFNo, 0, "query", licenseValueForForm).then((res) => {
          const selected = res.find((r) => r.value === incomingRMDFNo);
          if (selected?.ledgerTransactionId) {
           
            try {
              formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", selected.ledgerTransactionId);
            } catch (e) {
              // ignore if formRef not ready
            }

            // Build Incoming Product Article Number options from RMDF rawMaterials.productNumber
            const rmdfRawMaterials = Array.isArray(selected.rmdfRawMaterials) ? selected.rmdfRawMaterials : [];
            const rmdfProductOptions: Option[] = rmdfRawMaterials
              .filter((rm: any) => rm?.productNumber !== undefined && rm?.productNumber !== null)
              .map((rm: any) => {
                const productNumber = String(rm.productNumber);
                return {
                  value: productNumber,
                  label: productNumber,
                } as Option;
              });

            const uniqueRmdfProductOptions = rmdfProductOptions.filter(
              (item, index, self) => index === self.findIndex((t) => t.value === item.value)
            );

            setIncomingProductOptions(uniqueRmdfProductOptions);
            setDisableIncomingProduct(uniqueRmdfProductOptions.length === 0);
            // Do NOT auto-populate standards for RMDF (requirement)
          }
        });
      }
    } else if (incomingATCNo) {
    
      let certType = "";

        if (incomingATCNo?.startsWith("GOTS-")) {
          certType = "GOTS";
        } else if (incomingRMDFNo?.startsWith("RMDF-")) {
          certType = "RMDF";
        } else {
          certType = "ATU";
        }

        setSelectedCertType(certType);
      formRef?.current?.setFieldValue("certificateType", certType);
      setActiveSearchField("atc");
      setProductIncomingTcNo(incomingATCNo);
    
      if (licenseValueForForm) {
        getInputATCNoForSearch(incomingATCNo, 0, "query", licenseValueForForm).then((res) => {
          const selected = res.find((r) => r.value === incomingATCNo);
          if (selected?.atcId) {
           
            try {
              formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
            } catch (e) {
              // ignore if formRef not ready
            }
            fetchIncomingATCDetails(selected.atcId);
          }
        });
      }
    } else {
      setActiveSearchField(null);
      resetIncomingFields();
    }

  
    if (Array.isArray(componentData.tcIncomingTCStandard) && componentData.tcIncomingTCStandard.length > 0) {
      const normalized = normalizeStandards(componentData.tcIncomingTCStandard);
      setIncomingStandards(normalized);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", normalized);
    }

    if (Array.isArray(componentData.tcIncomingProductNo) && componentData.tcIncomingProductNo.length > 0) {
      
      const options: Option[] = componentData.tcIncomingProductNo.map((p: any) => ({ value: p, label: String(p) }));
      setIncomingProductOptions(options);
      setDisableIncomingProduct(false);
      formRef?.current?.setFieldValue("tcIncomingProductNo", componentData.tcIncomingProductNo);
    }
  }, [componentData.tcIncomingTCNo, componentData.tcIncomingATCNo, componentData.tcIncomingRMDFNo, componentData.incomingTcLicensingCode, componentData.licensingCode]);

  const StyledScrollBox = styled(Box)(({ theme }) => ({
    borderRadius: "12px",
    border: "1px solid #ccc",
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(1),
    height: "110px",
    overflowY: "auto",
    color: "#1C1C1C",
    fontSize: "0.875rem", 
    lineHeight: 1.4,
    paddingRight: theme.spacing(0.5),
  }));

  useEffect(() => {
    if (TE_ID) {
      fetchTcBaseDetails();
      fetchProductOptions();
    }
  }, [TE_ID]);

  const getCBLicensingCodes = async () => {
    try {
      const result: any = await dtrackItService.getCBLicensingCodes();
      const cbLicensing = result.data.map((t: any) => {
        const code = t.code_1.replace(/^CB-/, '');
        return {
          value: t.code_1, 
          label: code, 
        };
      });
      setLicensingCodes(cbLicensing);
    } catch (error) {
      console.error('Error fetching licensing codes:', error);
    }
  }; 

  useEffect(() => {
    getCBLicensingCodes();
  }, []);

  // Sync isFlagSellerState whenever componentData.isFlagSeller changes (e.g., when editing)
  useEffect(() => {
    if (componentData.isFlagSeller) {
      setIsFlagSellerState(componentData.isFlagSeller);
      try {
        formRef?.current?.setFieldValue("isFlagSeller", componentData.isFlagSeller);
      } catch (e) {
        // ignore if formRef not ready
      }
    }
  }, [componentData.isFlagSeller]);

  
const validationSchema = Yup.object().shape({
  tcAssociatedProductArticleNo: Yup.string().required("Required"),
  isFlagSeller: Yup.string().oneOf(['Yes', 'No']).required("Required"),
  certificateType: Yup.string().when('isFlagSeller', {
    is: 'No',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  licensingCode: Yup.string().when('isFlagSeller', {
    is: 'No',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingTCNo: Yup.string().when(['isFlagSeller', 'certificateType'], {
    is: (isFlagSeller: string, certificateType: string) => isFlagSeller === 'No' && certificateType === 'TC',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingATCNo: Yup.string().when(['isFlagSeller', 'certificateType'], {
    is: (isFlagSeller: string, certificateType: string) => isFlagSeller === 'No' && ['GOTS', 'ATU'].includes(certificateType),
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingRMDFNo: Yup.string().when(['isFlagSeller', 'certificateType'], {
    is: (isFlagSeller: string, certificateType: string) => isFlagSeller === 'No' && certificateType === 'RMDF',
    then: (schema) => schema.required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingProductNo: Yup.array().when('isFlagSeller', {
    is: 'No',
    then: (schema) => schema.of(Yup.string()).min(1, 'Select at least one incoming product article no').required('Required'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

  const handleCertificateTypeChange = (event: any) => {
    const value = event?.target?.value || "";
    setSelectedCertType(value); 
    formRef?.current?.setFieldValue("certificateType", value);
  
    // Issue 3: Reset all certificate-related fields when type changes
    formRef?.current?.setFieldValue("tcIncomingTCNo", "");
    formRef?.current?.setFieldValue("tcIncomingATCNo", "");
    formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
    formRef?.current?.setFieldValue("searchCertificateNumber", "");
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
    
    setProductIncomingTcNo("");
    setActiveSearchField(null);
    resetIncomingFields();
    
    // Propagate the changes to parent
    setComponentData({
      ...(componentData || {}),
      tcIncomingTCNo: "",
      tcIncomingATCNo: "",
      tcIncomingRMDFNo: "",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
    });
  };

  const handleLicensingValueChange = (event: any) => {
  const value = event?.target?.value || "";
  setSelectedLicensingCode(value);
  formRef?.current?.setFieldValue("licensingCode", value);
};

  // Accepts FormBuilder's onChange signature (value, formValues, setFieldValue)
  const handleIsFlagSellerChange = (value: any, formValues?: any, setFieldValue?: any) => {
    // value can be an event or direct value
    const newVal = value?.target?.value ?? value ?? "No";
    
    try {
      if (typeof setFieldValue === 'function') {
        setFieldValue('isFlagSeller', newVal);
      } else {
        formRef?.current?.setFieldValue('isFlagSeller', newVal);
      }
      
      // Update the UI state
      setIsFlagSellerState(newVal);

      // When switching to YES (Issue 1): set all non-flag fields to 'na'
      if (newVal === 'Yes') {
        formRef?.current?.setFieldValue("certificateType", "");
        formRef?.current?.setFieldValue("licensingCode", "");
        formRef?.current?.setFieldValue("searchCertificateNumber", "");
        formRef?.current?.setFieldValue("tcIncomingTCNo", "na");
        formRef?.current?.setFieldValue("tcIncomingATCNo", "na");
        formRef?.current?.setFieldValue("tcIncomingRMDFNo", "na");
        formRef?.current?.setFieldValue("tcIncomingProductNo", ["na"]);
        formRef?.current?.setFieldValue("tcIncomingTCStandard", ["na"]);
        formRef?.current?.setFieldValue("tcIncomingTcId", "na");
        formRef?.current?.setFieldValue("tcIncomingAtcId", "na");
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "na");
        
        setSelectedCertType("");
        setSelectedLicensingCode("");
        setProductIncomingTcNo("");
        setIncomingProductOptions([]);
        setIncomingStandards([]);
        setDisableIncomingProduct(true);
        setActiveSearchField(null);

        // Propagate to parent with 'na' values
        setComponentData({ 
          ...(componentData || {}), 
          isFlagSeller: 'Yes',
          tcIncomingTCNo: "na",
          tcIncomingATCNo: "na",
          tcIncomingRMDFNo: "na",
          tcIncomingProductNo: ["na"],
          tcIncomingTCStandard: ["na"],
          tcIncomingTcId: "na",
          tcIncomingAtcId: "na",
          tcIncomingLedgerTransactionId: "na",
          incomingTcLicensingCode: "na",
        });
      } else {
        // When switching to NO (Issue 2): clear all non-flag/non-article fields to empty
        formRef?.current?.setFieldValue("certificateType", "");
        formRef?.current?.setFieldValue("licensingCode", "");
        formRef?.current?.setFieldValue("searchCertificateNumber", "");
        formRef?.current?.setFieldValue("tcIncomingTCNo", "");
        formRef?.current?.setFieldValue("tcIncomingATCNo", "");
        formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
        formRef?.current?.setFieldValue("tcIncomingProductNo", []);
        formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
        formRef?.current?.setFieldValue("tcIncomingTcId", "");
        formRef?.current?.setFieldValue("tcIncomingAtcId", "");
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");

        setSelectedCertType("");
        setSelectedLicensingCode("");
        setProductIncomingTcNo("");
        setIncomingProductOptions([]);
        setIncomingStandards([]);
        setDisableIncomingProduct(true);
        setActiveSearchField(null);

        // Propagate to parent with empty values
        setComponentData({ 
          ...(componentData || {}), 
          isFlagSeller: 'No',
          tcIncomingTCNo: "",
          tcIncomingATCNo: "",
          tcIncomingRMDFNo: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          incomingTcLicensingCode: "",
        });
      }
    } catch (e) {
      // ignore
    }
  };

  // Accepts FormBuilder's onChange signature (value, formValues, setFieldValue)
  const handleAssociatedProductChange = (value: any, formValues?: any, setFieldValue?: any) => {
    const newVal = value?.target?.value ?? value?.value ?? value ?? "";
    try {
      if (typeof setFieldValue === 'function') {
        setFieldValue('tcAssociatedProductArticleNo', newVal);
      } else {
        formRef?.current?.setFieldValue('tcAssociatedProductArticleNo', newVal);
      }
    } catch (e) {
      // ignore if formRef not ready
    }
    // Propagate immediately to parent so parent Save sees the latest selection
    setComponentData({ ...(componentData || {}), tcAssociatedProductArticleNo: newVal });
  };

  useEffect(() => {
    const formValue = formRef?.current?.values?.licensingCode;
    if (formValue && formValue !== selectedLicensingCode) {
      setSelectedLicensingCode(formValue);
    }
  }, [formRef?.current?.values?.licensingCode]);
  
  

  const formConfig: FormConfigProps = {
    initialValues: {
      ...componentData,
      isFlagSeller: componentData.isFlagSeller || "No",
      certificateType: componentData.certificateType || (
        componentData.tcIncomingTCNo ? 'TC' :
        componentData.tcIncomingRMDFNo?.startsWith('RMDF-') ? 'RMDF' :
        componentData.tcIncomingATCNo?.startsWith('GOTS-') ? 'GOTS' :
        componentData.tcIncomingATCNo?.startsWith('ATU-') ? 'ATU' : ""
      ),
      licensingCode: componentData.incomingTcLicensingCode ? (componentData.incomingTcLicensingCode.startsWith("CB-") ? componentData.incomingTcLicensingCode : `CB-${componentData.incomingTcLicensingCode}`) : "", 
      tcIncomingTCStandard: normalizeStandards(componentData.tcIncomingTCStandard),
      tcIncomingProductNo: Array.isArray(componentData.tcIncomingProductNo)
        ? componentData.tcIncomingProductNo
        : componentData.tcIncomingProductNo
        ? [componentData.tcIncomingProductNo]
        : [],
      tcIncomingRMDFNo: componentData.tcIncomingRMDFNo || "",
      tcIncomingLedgerTransactionId: componentData.tcIncomingLedgerTransactionId || "",
      searchCertificateNumber: componentData.tcIncomingTCNo || componentData.tcIncomingATCNo || componentData.tcIncomingRMDFNo || "",
    },
    validationSchema,
    sections: [
      {
        fields: [
          {
            label: "Associated Product Article Number",
            name: "tcAssociatedProductArticleNo",
            type: "select",
            gridSize: { xs: 12, md: 6 },
            required: true,
            readOnly: hasSubmitTc,
            events: {
              onChange: handleAssociatedProductChange,
            },
            props: {
              options: productOptions,
              enableSearch: true,
              placeholder: "Select Product Article No",
              fullWidth: true,
            },
          },
          {
            label: "Is Seller a Farm",
            name: "isFlagSeller",
            type: "radio",
            gridSize: { xs: 12, md: 6 },
            required: true,
            // when changed, toggle UI state
            events: {
              onChange: handleIsFlagSellerChange,
            },
            // events:{
            //   onChange:
            // },
            props: {
              options: [
                { value: "Yes", label: "Yes" },
                { value: "No", label: "No" },
              ],
            },
          },
          {
            type: "component",
            label: "",
            name: "infoMessage",
            required: false,
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box sx={{ color: 'text.secondary', mt: -2, mb: 2, fontSize: '0.875rem' }}>
                Choose the option and search the certificate number below for the Product Incoming TC number.
              </Box>
            ),
          },
        ],
      },
      {
        fields: [
          {
            label: "Choose Type of Certificate",
            name: "certificateType",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: isFlagSellerState === 'Yes' ? [] : certificateTypes,
              onChange: handleCertificateTypeChange,
              select: true,
              native: false,
              fullWidth: true,
              disabled: isFlagSellerState === 'Yes',
              placeholder: isFlagSellerState === 'Yes' ? 'NA' : 'Select Certificate Type',
            },
          },
          {
            label: "Choose CB Licensing Code",
            name: "licensingCode",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: licensingCodes,
              select: true,
              native: false,
              fullWidth: true,
              enableSearch: true,
              onChange: handleLicensingValueChange,
              disabled: isFlagSellerState === 'Yes',
              placeholder: isFlagSellerState === 'Yes' ? 'NA' : 'Select Licensing Code',
            },
          },
          {
            label: "Search for the Certificate Number",
            name: "searchCertificateNumber",
            type: "search",
            required: selectedCertType ? true : false,
            gridSize: { xs: 12, md: 4 },
            props: {
              onChange: handleCertificateNumberChange,
              onSearch: (query: string, offset: number) => {
                // Prevent API calls when seller is a farm or when required fields are missing
                if (isFlagSellerState === 'Yes' || !selectedCertType || !selectedLicensingCode) {
                  return Promise.resolve([]);
                }
                
                // Prevent API calls for "na" value (special value for farm sellers)
                if (query === 'na' || !query || query.trim() === '') {
                  return Promise.resolve([]);
                }
                
                if (selectedCertType === 'TC') {
                  return getTCNoforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'RMDF') {
                  return getRMDFforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
                  return getInputATCNoForSearch(query, offset, "query", selectedLicensingCode);
                }
                return Promise.resolve([]);
              },
              displayKey: "label",
              fullWidth: true,
              disabled: isFlagSellerState === 'Yes' ? true : (!selectedCertType || !selectedLicensingCode),
              placeholder: isFlagSellerState === 'Yes' ? 'NA' : (selectedCertType === 'TC' ? 'Search TC Number' : selectedCertType === 'RMDF' ? 'Search RMDF Number' : 'Search ATU Number'),
              value: productIncomingTcNo,
            },
          },
        ],
      },
      {
        fields: [
          {
            label: "Selected Product Incoming TC No",
            name: "productIncomingTcNo",
            type: "text",
            required: false,
            readOnly: true,
            gridSize: { xs: 12, md: 6 },
            props: {
              value: productIncomingTcNo,
              fullWidth: true,
              disabled: isFlagSellerState === 'Yes',
              placeholder: isFlagSellerState === 'Yes' ? 'NA' : 'Selected Product Incoming TC No',
              sx: {
                backgroundColor: "#e0e0e0",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#e0e0e0",
                  "& fieldset": {
                    borderColor: "#bdbdbd",
                  },
                },
              },
            },
          },
          {
            label: "Incoming Product Article Number",
            name: "tcIncomingProductNo",
            type: "select",
            gridSize: { xs: 12, md: 6 },
            required: true,
            props: {
              options: isFlagSellerState === 'Yes' 
                ? [{ value: 'na', label: 'na' }, ...incomingProductOptions]
                : incomingProductOptions,
              multiple: true,
              enableSearch: true,
              placeholder: isFlagSellerState === 'Yes' ? 'NA' : (disableIncomingProduct ? 'Enter TC No or ATC No first' : 'Select Incoming Product No'),
              fullWidth: true,
              disabled: isFlagSellerState === 'Yes' ? true : disableIncomingProduct,
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
  const licensingRaw = values.licensingCode || componentData.incomingTcLicensingCode || componentData.licensingCode || "";
  const licensingCodeOnly = String(licensingRaw).replace(/^CB-/, "");

      // If flagged as seller farm, set other fields to 'na' as per requirement
      const isSeller = values.isFlagSeller === 'Yes';

      const payload = isSeller
        ? {
            tcAssociatedProductArticleNo: values.tcAssociatedProductArticleNo,
            tcIncomingTCNo: "na",
            tcIncomingATCNo: "na",
            tcIncomingRMDFNo: "na",
            tcIncomingProductNo: ["na"],
            tcIncomingTCStandard: ["na"],
            tcIncomingTcId: "na",
            tcIncomingAtcId: "na",
            tcIncomingLedgerTransactionId: "na",
            incomingTcLicensingCode: "na",
            isFlagSeller: "Yes",
          }
        : {
            tcAssociatedProductArticleNo: values.tcAssociatedProductArticleNo,
            tcIncomingTCNo: values.tcIncomingTCNo,
            tcIncomingATCNo: values.tcIncomingATCNo,
            tcIncomingRMDFNo: values.tcIncomingRMDFNo,
            tcIncomingProductNo: values.tcIncomingProductNo,
            tcIncomingTCStandard: values.tcIncomingTCStandard,
            tcIncomingTcId: values.tcIncomingTcId || componentData.tcIncomingTcId || "",
            tcIncomingAtcId: values.tcIncomingAtcId || componentData.tcIncomingAtcId || "",
            tcIncomingLedgerTransactionId: values.tcIncomingLedgerTransactionId || componentData.tcIncomingLedgerTransactionId || "",
            incomingTcLicensingCode: licensingCodeOnly,
            isFlagSeller: "No",
          };
      
      // Set component data without UI-only fields
      setComponentData(payload);
    },
  };

  return <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />;
};

export default IncomingTcInfo;